/**
 * SurveyRunner — VER-108 → VER-109
 *
 * Renders a SurveyDefinition to "take" or preview. VER-109 extends it to the
 * section-based model with six question types (likert / single / multi / freeText
 * / vas / nps) and branching-aware visibility: questions/sections hidden by a
 * branching rule are not shown and do not count toward progress or the score.
 *
 * SAFETY:
 *  - The survey's `licence` text is ALWAYS rendered when present.
 *  - Cut-off guidance is phrased as neutral wellbeing guidance, NEVER a diagnosis.
 *
 * FRONTEND-ONLY — responses live in local state and are not persisted. The
 * builder reuses this component for the live participant preview.
 */
import React, { useMemo, useRef, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    LinearProgress,
    Paper,
    Radio,
    RadioGroup,
    Slider,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { CheckCircle, CheckCircleOutline, InfoOutlined, ReplayOutlined } from "@mui/icons-material";
import type { ScaleOption, SurveyDefinition, SurveyQuestion, SurveyResponseValues } from "./survey.types";
import { effectiveOptions } from "./survey.types";
import { isAnswered, scoreSurvey, SurveyScoreResult } from "./scoring";
import { resolveVisibility } from "./branching";

export type SurveyRunnerProps = {
    survey: SurveyDefinition;
    /** Optional banner shown above the survey (e.g. "Preview" framing). */
    previewBanner?: React.ReactNode;
    /** Called when the respondent leaves the runner. */
    onExit?: () => void;
    /**
     * Called when the respondent submits a COMPLETE survey.
     *
     * `values` carries the raw per-question answers alongside the aggregate result, because
     * persistence needs item-level grain (VER-114): `SurveyScoreResult` deliberately exposes
     * only totals, so a caller that must store answers cannot reconstruct them from it.
     */
    onScored?: (result: SurveyScoreResult, values: SurveyResponseValues) => void | Promise<void>;
    /** Optional node rendered inside the score panel once the survey is scored. */
    scoreFooter?: React.ReactNode;
    /**
     * True while the caller is persisting the scored response.
     *
     * Without this the score button stayed live after submission: `onScored` used to have no side
     * effect, but it now POSTs, and the capture route is a plain create with no idempotency key. A
     * second click on a slow connection wrote a duplicate SurveyResponse, which the outcome report
     * then read as a PAIRED administration — inflating `participantsPaired`, the count that also
     * gates anonymity suppression, with a zero-difference pair.
     */
    submitting?: boolean;
    /**
     * True once this run has been persisted.
     *
     * `submitting` alone only covers the in-flight window: the moment the save succeeded the button
     * re-enabled, and a second click POSTed a second create — the capture route has no idempotency
     * key and no uniqueness on (participant, programme, template, administration). The duplicate is
     * then read as a PAIRED administration, inflating `participantsPaired`, which also gates
     * anonymity suppression, with a zero-difference pair. A saved run stays locked; a FAILED one
     * does not, so the practitioner can still retry.
     */
    persisted?: boolean;
};

const emptyResponses = (survey: SurveyDefinition): SurveyResponseValues => {
    const acc: SurveyResponseValues = {};
    for (const section of survey.sections) {
        for (const q of section.questions) {
            acc[q.id] = q.type === "multi" ? [] : "";
        }
    }
    return acc;
};

// --- shared choice-grid (likert + nps) ------------------------------------
const ChoiceGrid = ({
    question,
    options,
    current,
    onSelect,
}: {
    question: SurveyQuestion;
    options: ScaleOption[];
    current: number | "";
    onSelect: (value: number | null) => void;
}): JSX.Element => (
    <ToggleButtonGroup
        exclusive
        value={current === "" ? null : current}
        onChange={(_, val) => onSelect(val)}
        aria-label={question.text || question.id}
        sx={{
            display: "grid",
            width: "100%",
            gap: 1,
            gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: `repeat(${Math.min(options.length, 11)}, minmax(0, 1fr))`,
            },
            "& .MuiToggleButtonGroup-grouped": {
                m: 0,
                flexDirection: "column",
                gap: 0.5,
                py: 1.25,
                lineHeight: 1.3,
                border: "1px solid",
                borderColor: "borderShade.main",
                borderRadius: (theme) => `${theme.shape.borderRadius}px !important`,
                "&:hover": { backgroundColor: "secondaryLightest.main" },
                "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "common.white",
                    borderColor: "primary.main",
                    "&:hover": { backgroundColor: "primary.dark" },
                },
            },
        }}
    >
        {options.map((option) => (
            <ToggleButton key={`${question.id}-${option.value}`} value={option.value} aria-label={option.label}>
                <Typography variant="h5" component="span" sx={{ fontWeight: 700 }}>
                    {option.value}
                </Typography>
                <Typography
                    variant="caption"
                    component="span"
                    sx={{ textTransform: "none", color: "inherit", opacity: 0.85 }}
                >
                    {option.label}
                </Typography>
            </ToggleButton>
        ))}
    </ToggleButtonGroup>
);

// --- per-type input --------------------------------------------------------
const QuestionInput = ({
    survey,
    question,
    value,
    onChange,
}: {
    survey: SurveyDefinition;
    question: SurveyQuestion;
    value: SurveyResponseValues[string];
    onChange: (value: SurveyResponseValues[string]) => void;
}): JSX.Element => {
    const options = effectiveOptions(survey, question);

    switch (question.type) {
        case "single":
            return (
                <RadioGroup
                    value={value === "" ? "" : String(value)}
                    onChange={(e) => onChange(Number(e.target.value))}
                >
                    {options.map((o) => (
                        <FormControlLabel
                            key={`${question.id}-${o.value}`}
                            value={String(o.value)}
                            control={<Radio />}
                            label={o.label}
                        />
                    ))}
                </RadioGroup>
            );

        case "multi": {
            const selected = Array.isArray(value) ? value : [];
            const toggle = (optValue: number) => {
                const next = selected.includes(optValue)
                    ? selected.filter((v) => v !== optValue)
                    : [...selected, optValue];
                onChange(next);
            };
            return (
                <FormGroup>
                    {options.map((o) => (
                        <FormControlLabel
                            key={`${question.id}-${o.value}`}
                            control={<Checkbox checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />}
                            label={o.label}
                        />
                    ))}
                </FormGroup>
            );
        }

        case "freeText":
            return (
                <TextField
                    fullWidth
                    multiline={question.freeText?.multiline ?? true}
                    minRows={question.freeText?.multiline === false ? 1 : 3}
                    placeholder={question.freeText?.placeholder ?? "Type your answer"}
                    inputProps={{ maxLength: question.freeText?.maxLength }}
                    value={typeof value === "string" ? value : ""}
                    onChange={(e) => onChange(e.target.value)}
                />
            );

        case "vas": {
            const min = question.vas?.min ?? 0;
            const max = question.vas?.max ?? 100;
            const step = question.vas?.step ?? 1;
            const numeric = typeof value === "number" ? value : min;
            return (
                <Box px={1}>
                    <Slider
                        value={numeric}
                        min={min}
                        max={max}
                        step={step}
                        valueLabelDisplay="auto"
                        onChange={(_, val) => onChange(Array.isArray(val) ? val[0] : val)}
                        aria-label={question.text || question.id}
                    />
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="textSecondary">
                            {question.vas?.minLabel ?? min}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {question.vas?.maxLabel ?? max}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        case "nps":
        case "likert":
        default:
            return (
                <ChoiceGrid
                    question={question}
                    options={options}
                    current={typeof value === "number" ? value : ""}
                    onSelect={(v) => {
                        if (v !== null) {
                            onChange(v);
                        }
                    }}
                />
            );
    }
};

export const SurveyRunner = ({
    survey,
    previewBanner,
    onExit,
    onScored,
    scoreFooter,
    submitting = false,
    persisted = false,
}: SurveyRunnerProps): JSX.Element => {
    const [values, setValues] = useState<SurveyResponseValues>(() => emptyResponses(survey));
    const [submitted, setSubmitted] = useState(false);
    /** Synchronous guard for the save click — see `persisted` on the props. */
    const inFlight = useRef(false);

    const result = useMemo(() => scoreSurvey(survey, values), [survey, values]);
    const { hiddenSectionIds, hiddenQuestionIds } = useMemo(() => resolveVisibility(survey, values), [survey, values]);

    const setAnswer = (questionId: string, value: SurveyResponseValues[string]) =>
        setValues((prev) => ({ ...prev, [questionId]: value }));

    const handleReset = () => {
        setValues(emptyResponses(survey));
        setSubmitted(false);
    };

    const orderedSections = [...survey.sections].sort((a, b) => a.order - b.order);
    let runningIndex = 0;

    return (
        <Box>
            {previewBanner}

            {survey.code ? (
                <Typography variant="overline" color="primary" sx={{ letterSpacing: 1.2 }}>
                    {survey.code}
                </Typography>
            ) : null}
            <Typography variant="h3" component="h1" color="textPrimary" mb={1}>
                {survey.title || "Untitled survey"}
            </Typography>
            {survey.instructions ? (
                <Typography variant="body1" color="textSecondary" mb={3} sx={{ maxWidth: 720 }}>
                    {survey.instructions}
                </Typography>
            ) : null}

            {/* Progress */}
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
                    <Typography variant="subtitle1" color="textPrimary">
                        Progress
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {result.answered} of {result.total} answered
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={result.total > 0 ? (result.answered / result.total) * 100 : 0}
                    sx={{ height: 8, borderRadius: 4 }}
                />
            </Box>

            {/* Sections */}
            <Stack spacing={4}>
                {orderedSections
                    .filter((section) => !hiddenSectionIds.has(section.id))
                    .map((section) => {
                        const visibleQuestions = [...section.questions]
                            .sort((a, b) => a.order - b.order)
                            .filter((q) => !hiddenQuestionIds.has(q.id));
                        if (visibleQuestions.length === 0) {
                            return null;
                        }
                        return (
                            <Box key={section.id}>
                                {orderedSections.length > 1 || section.title ? (
                                    <Box mb={2}>
                                        <Typography variant="h5" color="textPrimary">
                                            {section.title || "Section"}
                                        </Typography>
                                        {section.description ? (
                                            <Typography variant="body2" color="textSecondary">
                                                {section.description}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                ) : null}
                                <Stack spacing={2}>
                                    {visibleQuestions.map((question) => {
                                        runningIndex += 1;
                                        const current = values[question.id];
                                        const answered = isAnswered(current);
                                        return (
                                            <Paper
                                                key={question.id}
                                                elevation={0}
                                                sx={{
                                                    p: { xs: 2, sm: 3 },
                                                    border: "1px solid",
                                                    borderColor: answered ? "primary.main" : "borderShade.main",
                                                    borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                                                    transition: "border-color 0.15s ease-in-out",
                                                }}
                                            >
                                                <Box display="flex" alignItems="flex-start" gap={1.5} mb={2}>
                                                    <Avatar
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            fontSize: (theme) => theme.typography.body2.fontSize,
                                                            fontWeight: 700,
                                                            bgcolor: answered
                                                                ? "primary.main"
                                                                : "secondaryLightest.main",
                                                            color: answered ? "common.white" : "text.secondary",
                                                        }}
                                                    >
                                                        {answered ? (
                                                            <CheckCircle sx={{ fontSize: 18 }} />
                                                        ) : (
                                                            runningIndex
                                                        )}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography
                                                            variant="subtitle1"
                                                            color="textPrimary"
                                                            sx={{ pt: 0.25 }}
                                                        >
                                                            {question.text || "Untitled question"}
                                                            {question.required ? null : (
                                                                <Typography
                                                                    component="span"
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                    sx={{ ml: 1 }}
                                                                >
                                                                    (optional)
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <QuestionInput
                                                    survey={survey}
                                                    question={question}
                                                    value={current}
                                                    onChange={(v) => setAnswer(question.id, v)}
                                                />
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        );
                    })}
            </Stack>

            {/* Actions */}
            <Box display="flex" justifyContent="space-between" alignItems="center" pt={3}>
                <Button
                    variant="text"
                    onClick={handleReset}
                    startIcon={<ReplayOutlined />}
                    disabled={result.answered === 0}
                >
                    Reset answers
                </Button>
                <Box display="flex" gap={2}>
                    {onExit ? (
                        <Button variant="outlined" onClick={onExit}>
                            Close
                        </Button>
                    ) : null}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            // The ref, not `submitting`, is what makes this safe: `submitting` is
                            // the parent's state, so two clicks landing before React re-renders
                            // both read the old `false` and both fire. A ref updates synchronously.
                            if (submitting || persisted || inFlight.current) return;
                            inFlight.current = true;
                            setSubmitted(true);
                            try {
                                if (result.complete && onScored) {
                                    await onScored(result, values);
                                }
                            } finally {
                                inFlight.current = false;
                            }
                        }}
                        disabled={!result.complete || submitting || persisted}
                        startIcon={<CheckCircleOutline />}
                    >
                        {submitting
                            ? "Saving…"
                            : persisted
                              ? "Saved"
                              : result.complete
                                ? "Score survey"
                                : `Answer all ${result.total} questions`}
                    </Button>
                </Box>
            </Box>

            {/* Score panel */}
            {submitted && result.complete ? (
                <Box
                    sx={{
                        mt: 4,
                        p: 3,
                        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                        backgroundColor: (theme) => theme.palette.success.light,
                        border: "1px solid",
                        borderColor: "success.main",
                    }}
                >
                    <Typography variant="h5" color="success.dark" mb={2}>
                        Score
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                                Raw score (sum of items)
                            </Typography>
                            <Typography variant="h4" color="success.dark">
                                {result.rawScore}
                                <Typography variant="body2" component="span" color="textSecondary" sx={{ ml: 1 }}>
                                    range {survey.scoring.rawRange[0]}–{survey.scoring.rawRange[1]}
                                </Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                                Final score{survey.scoring.multiplier ? ` (×${survey.scoring.multiplier})` : ""}
                            </Typography>
                            <Typography variant="h4" color="success.dark">
                                {result.finalScore}
                            </Typography>
                        </Grid>
                    </Grid>

                    {result.cutoffLabel ? (
                        <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                Wellbeing guidance: {result.cutoffLabel}. This is general guidance only and is not a
                                diagnosis.
                            </Typography>
                        </Alert>
                    ) : null}

                    {scoreFooter ? <Box sx={{ mt: 2 }}>{scoreFooter}</Box> : null}
                </Box>
            ) : null}

            {/* Licence — ALWAYS shown when present */}
            {survey.licence ? (
                <>
                    <Divider sx={{ mt: 4, mb: 2 }} />
                    <Typography variant="caption" color="textSecondary" display="block">
                        Licence: {survey.licence}
                    </Typography>
                </>
            ) : null}
        </Box>
    );
};
